import React, { useState, useEffect, useCallback } from 'react';
import { devLog, devError } from '../../utils/devLogger';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useLang } from '../../context/LangContext';
import { useBidContext } from '../../context/BidContext';
import { useUserAuth } from '../../context/UserAuthContext';
import { preBid, getPreBidDetail } from '../../lib/api/user/userApi';
import { PreBidDetailData } from '../../lib/api/user/userApi.types';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import TossPaymentWidget from '../../components/TossPaymentWidget';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-bottom: 140px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 16px 0;
  gap: 24px;
`;

const InfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 32px 0;
  margin-bottom: 8px;
  border-top: 1px solid #f3f3f3;
  border-bottom: 1px solid #f3f3f3;
`;

const LotImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  background: #f0f0f0;
`;

const LotInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  justify-content: flex-start;
`;

const LotName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  line-height: 1.4;
  text-align: left;
  align-self: flex-start;
`;

const LotId = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
  text-align: left;
  align-self: flex-start;
`;

const AmountSection = styled.div`
  background: white;
  border-radius: 12px;
  padding-top: 20px;
  margin-bottom: 8px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
  color: #7a6647;
  padding-top: 8px;
`;

const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 75px;
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  z-index: 999;
  border-top: 1px solid #e9ecef;

  @media (min-width: 768px) {
    bottom: 0;
    padding: 20px 24px;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;

  @media (min-width: 768px) {
    max-width: 1200px;
  }
`;

const ShippingNotice = styled.div`
  font-size: 12px;
  color: #666;
  text-align: center;
  margin-bottom: 12px;
  line-height: 1.4;
`;

const shoppingRequestTexts = {
  ko: {
    title: '상품 구매',
    amountSection: '구매가',
    purchaseAmountLabel: '구매가',
    purchaseAmountPlaceholder: '구매가를 선택해주세요',
    totalAmount: '총 합계',
    currency: 'KRW',
    purchaseButton: '구매하기',
    shippingNotice: '배송은 영업일 기준 7일 이내 발송되며,\n평균 배송일은 공휴일 제외 1-3일입니다'
  },
  en: {
    title: 'Purchase Product',
    amountSection: 'Purchase Price',
    purchaseAmountLabel: 'Purchase Price',
    purchaseAmountPlaceholder: 'Please select purchase price',
    totalAmount: 'Total Amount',
    currency: 'USD',
    purchaseButton: 'Purchase',
    shippingNotice: 'Delivery will be shipped within 7 business days.\nAverage delivery time is 1-3 days excluding holidays'
  }
};

function ShoppingRequest() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { lang } = useLang();
  const { currentLotData, setCurrentLotData } = useBidContext();
  const { isLoggedIn, user } = useUserAuth();
  const texts = shoppingRequestTexts[lang];
  const [showPayment, setShowPayment] = useState(false);
  const [orderId, setOrderId] = useState('');

  useEffect(() => {
    const fetchLotDataIfNeeded = async () => {
      if (!currentLotData && id) {
        try {
          devLog('🛒 [ShoppingRequest] API에서 데이터 가져오기:', id);
          const response = await getPreBidDetail(parseInt(id));
          
          if (response.data.statusCode === 200 && response.data.data) {
            const lotData: any = response.data.data;
            
            const transformedLotData: PreBidDetailData = {
              index: lotData.index,
              code: lotData.code || `LOT.${lotData.index}`,
              name: lotData.name,
              description: lotData.description || '',
              images: lotData.images || [],
              startPrice: lotData.startPrice || 0,
              exchangeRate: lotData.exchangeRate || 1400,
              startPriceUsd: lotData.startPriceUsd || 0,
              recommendNo: lotData.recommendNo || null,
              likedUserIndexes: lotData.likedUserIndexes || [],
              likeCount: lotData.likeCount || 0,
              createBy: lotData.createBy || '',
              updateBy: lotData.updateBy || '',
              createAt: lotData.createAt || '',
              updateAt: lotData.updateAt || '',
              category: lotData.category || { code: '', name: '', krName: '' },
              subCategory: lotData.subCategory || { code: '', name: '', krName: '' },
              auctioneer: {
                index: lotData.auctioneer?.index || 0,
                name: lotData.auctioneer?.name || '',
                description: lotData.auctioneer?.description || '',
                profilePic: lotData.auctioneer?.profilePic || '',
                term: lotData.auctioneer?.term
              },
              auction: {
                index: lotData.auction?.index || 0,
                name: lotData.auction?.name || '',
                description: lotData.auction?.description || '',
                url: lotData.auction?.url || '',
                auctionLotNo: lotData.auction?.auctionLotNo || 0,
                preBidStartAt: lotData.auction?.preBidStartAt || '',
                preBidEndAt: lotData.auction?.preBidEndAt || '',
                auctionStartAt: lotData.auction?.auctionStartAt || '',
                auctionEndAt: lotData.auction?.auctionEndAt || '',
                paymentDueAt: lotData.auction?.paymentDueAt || '',
                auctionFeeRate: lotData.auction?.auctionFeeRate || 0,
                platformFeeRate: lotData.auction?.platformFeeRate || 0,
                createAt: lotData.auction?.createAt || '',
                updateAt: lotData.auction?.updateAt || null,
                biddingRanges: lotData.auction?.biddingRanges || []
              },
              preBids: lotData.preBids || []
            };
            
            setCurrentLotData(transformedLotData);
          } else {
            toast.error(lang === 'ko' ? '상품 정보를 불러올 수 없습니다.' : 'Unable to load product information.');
            navigate(-1);
          }
        } catch (error) {
          devError('🛒 [ShoppingRequest] 상품 정보 조회 실패:', error);
          toast.error(lang === 'ko' ? '상품 정보를 불러오는 중 오류가 발생했습니다.' : 'An error occurred while loading product information.');
          navigate(-1);
        }
      }
    };

    fetchLotDataIfNeeded();
  }, [currentLotData, id, setCurrentLotData, navigate, lang]);



  // 총 결제 금액 계산
  const calculateTotalAmount = () => {
    return currentLotData?.startPrice || 0;
  };

  const formatNumber = (value: number) => {
    if (lang === 'ko') {
      return value.toLocaleString();
    } else {
      const exchangeRate = currentLotData?.exchangeRate || 1409.09;
      const usdValue = Math.round((value / exchangeRate) * 100) / 100;
      return usdValue.toFixed(2);
    }
  };

  const getImageSrc = (imageUrl?: string | null) => {
    if (!imageUrl || imageUrl.trim() === "") {
      return "/common/no_image.svg";
    }
    
    if (imageUrl.startsWith("http") || imageUrl.startsWith("blob:")) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith("/")) {
      return `${process.env.REACT_APP_API_HOST}${imageUrl}`;
    }
    
    return `${process.env.REACT_APP_API_HOST}/${imageUrl}`;
  };

  const handlePurchase = () => {
    if (!isLoggedIn) {
      toast.error(lang === 'ko' ? '로그인이 필요합니다.' : 'Login is required.');
      return;
    }

    if (!currentLotData?.index || !currentLotData?.startPrice) {
      toast.error(lang === 'ko' ? '구매 정보가 올바르지 않습니다.' : 'Purchase information is invalid.');
      return;
    }

    // 주문 ID 생성
    const newOrderId = `ORDER_${Date.now()}_${currentLotData.index}`;
    setOrderId(newOrderId);
    setShowPayment(true);
  };

  const handlePaymentSuccess = async (paymentData: any) => {
    try {

      const response = await preBid(currentLotData!.index, {
        price: currentLotData!.startPrice,
        paymentKey: paymentData.paymentKey || paymentData.orderId
      });

      if (response.data.statusCode === 200 || response.data.statusCode === 201) {
        toast.success(lang === 'ko' ? '구매가 완료되었습니다!' : 'Purchase completed successfully!');
        setTimeout(() => {
          navigate('/my-page/bid-history');
        }, 1500);
      } else {
        const errorMessage = response.data.message || (lang === 'ko' ? '구매 처리에 실패했습니다.' : 'Purchase processing failed.');
        toast.error(errorMessage);
      }
    } catch (error: any) {
      devError('🛒 구매 요청 실패:', error);
      let errorMessage = lang === 'ko' ? '구매 처리 중 오류가 발생했습니다.' : 'An error occurred during purchase processing.';
      
      const apiErrorMessage = error?.response?.data?.error?.customMessage || error?.response?.data?.message;
      if (apiErrorMessage) {
        errorMessage = apiErrorMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    }
  };

  const handlePaymentFail = (error: any) => {
    devError('🛒 결제 실패:', error);
    setShowPayment(false);
    // 사용자가 취소한 경우는 메시지 표시 안함
    if (error.code !== 'USER_CANCEL') {
      toast.error(lang === 'ko' ? '결제 중 오류가 발생했습니다.' : 'An error occurred during payment.');
    }
  };

  const Layout = (
    <MainLayout currentPage="home">
      <CenteredLayout>
        <ProjectAppbar 
          currentPage="home"
          title={texts.title}
          showBackButton={true}
          onBack={() => navigate(-1)}
        />
        <Container>
          <ContentContainer>
            <InfoSection>
              <LotImage 
                src={currentLotData?.images?.[0] ? getImageSrc(currentLotData.images[0]) : "/common/no_image.svg"} 
                alt="Product Image"
                onError={(e) => {
                  e.currentTarget.src = '/common/no_image.svg';
                }}
              />
              <LotInfoContainer>
                <LotName>{currentLotData?.name || '상품 정보를 불러올 수 없습니다.'}</LotName>
              </LotInfoContainer>
            </InfoSection>

            <div>
              <FormTitle>{texts.amountSection}</FormTitle>
              <AmountSection>
                <TotalRow>
                  <span>판매가</span>
                  <span>{texts.currency} {currentLotData?.startPrice ? formatNumber(currentLotData.startPrice) : '-'}</span>
                </TotalRow>
              </AmountSection>
            </div>
          </ContentContainer>
          
          <FixedButtonContainer>
            <ButtonWrapper>
              {!showPayment && (
                <ShippingNotice>
                  {texts.shippingNotice}
                </ShippingNotice>
              )}
              {showPayment && currentLotData && currentLotData.startPrice ? (
                <TossPaymentWidget
                  amount={calculateTotalAmount()}
                  orderId={orderId}
                  orderName={currentLotData.name}
                  customerName="구매자"
                  customerEmail="customer@example.com"
                  onSuccess={handlePaymentSuccess}
                  onFail={handlePaymentFail}
                />
              ) : (
                <PrimaryButton 
                  onClick={handlePurchase}
                  disabled={!currentLotData?.startPrice || !isLoggedIn}
                  title={!isLoggedIn ? (lang === 'ko' ? '로그인이 필요합니다' : 'Login required') : undefined}
                >
                  {!isLoggedIn 
                    ? (lang === 'ko' ? '로그인 후 구매하기' : 'Login to Purchase')
                    : texts.purchaseButton
                  }
                </PrimaryButton>
              )}
            </ButtonWrapper>
          </FixedButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={Layout}
      desktopView={Layout}
    />
  );
}

export default ShoppingRequest;
